import { useMemo, useState, type FormEvent } from "react";
import type { PackagePlan, ResellerProfile } from "../types";

const emptyForm = {
  username: "",
  password: "",
  email: "",
  company: "",
  packageId: "",
  maxAccounts: 100,
  maxDomains: 500,
  diskMb: 512000,
  maxDatabases: 500,
  maxEmails: 1000,
  productName: "Hosting Panel",
  nameservers: "ns1.example.com\nns2.example.com",
  supportUrl: ""
};

function money(n: number) {
  return `$${Number(n || 0).toFixed(2)}`;
}

/**
 * W4 — WHM Reseller Center (root only)
 * Assign Reseller Silver/Gold/Platinum plans (account pools + pricing).
 */
export function ResellersPage({
  resellers,
  packages = [],
  onCreate,
  onUpdate,
  onRefresh
}: {
  resellers: ResellerProfile[];
  /** Reseller catalog plans only */
  packages?: PackagePlan[];
  onCreate: (data: typeof emptyForm) => Promise<void>;
  onUpdate: (
    id: string,
    data: Partial<{
      company: string;
      packageId: string | null;
      maxAccounts: number;
      maxDomains: number;
      diskMb: number;
      maxDatabases: number;
      maxEmails: number;
      productName: string;
      nameservers: string[];
      supportUrl: string;
    }>
  ) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const resellerPlans = useMemo(
    () =>
      packages.filter(
        (p) => p.planKind === "reseller" && p.active !== false
      ),
    [packages]
  );

  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof emptyForm, v: string | number) =>
    setForm((f) => ({ ...f, [k]: v }));

  const applyPlan = (packageId: string) => {
    const p = resellerPlans.find((x) => x.id === packageId);
    if (!p) {
      set("packageId", packageId);
      return;
    }
    setForm((f) => ({
      ...f,
      packageId: p.id,
      maxAccounts: p.maxAccounts || 100,
      maxDomains: p.maxDomains,
      diskMb: p.diskMb,
      maxDatabases: p.maxDatabases,
      maxEmails: p.maxEmails
    }));
  };

  const parseNs = (raw: string) =>
    raw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const startEdit = (r: ResellerProfile) => {
    setEditId(r.userId);
    setForm({
      username: r.username || "",
      password: "",
      email: "",
      company: r.company,
      packageId: r.packageId || "",
      maxAccounts: r.maxAccounts,
      maxDomains: r.maxDomains,
      diskMb: r.diskMb,
      maxDatabases: r.maxDatabases,
      maxEmails: r.maxEmails,
      productName: r.productName,
      nameservers: (r.nameservers || []).join("\n"),
      supportUrl: r.supportUrl || ""
    });
    setShow(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      if (editId) {
        await onUpdate(editId, {
          company: form.company,
          packageId: form.packageId || null,
          maxAccounts: form.maxAccounts,
          maxDomains: form.maxDomains,
          diskMb: form.diskMb,
          maxDatabases: form.maxDatabases,
          maxEmails: form.maxEmails,
          productName: form.productName,
          nameservers: parseNs(form.nameservers),
          supportUrl: form.supportUrl
        });
        setMsg("Reseller plan / pools updated");
      } else {
        await onCreate(form);
        setMsg("Reseller created — they can log in and manage cPanel customers");
      }
      setShow(false);
      setEditId(null);
      setForm(emptyForm);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const pct = (used: number, max: number) =>
    Math.min(100, Math.round((used / Math.max(1, max)) * 100));

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Reseller Center</h1>
          <p>
            Assign a <strong>Reseller plan</strong> (Silver / Gold / Platinum).
            That plan caps how many <strong>cPanel accounts</strong> they can
            create — same idea as WHM reseller packages.
          </p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefresh()}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary btn-inline"
            onClick={() => {
              setEditId(null);
              const def = resellerPlans[0];
              setForm({
                ...emptyForm,
                packageId: def?.id || "",
                maxAccounts: def?.maxAccounts || 100,
                maxDomains: def?.maxDomains || 500,
                diskMb: def?.diskMb || 512000,
                maxDatabases: def?.maxDatabases || 500,
                maxEmails: def?.maxEmails || 1000
              });
              setShow((v) => !v);
            }}
          >
            {show && !editId ? "Close" : "Create a Reseller"}
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error && !show ? <div className="error-banner">{error}</div> : null}

      {resellerPlans.length > 0 ? (
        <div className="pkg-grid" style={{ marginBottom: 16 }}>
          {resellerPlans.map((p) => (
            <section key={p.id} className="card pkg-card">
              <div className="pkg-head">
                <div className="card-value" style={{ fontSize: 18 }}>
                  {p.name}
                </div>
                <span className="badge primary">
                  {p.maxAccounts.toLocaleString()} accounts
                </span>
              </div>
              <p className="card-meta">{p.description}</p>
              <dl className="kv compact">
                <dt>monthly</dt>
                <dd className="mono">{money(p.priceMonthly)}</dd>
                <dt>yearly</dt>
                <dd className="mono">{money(p.priceYearly)}</dd>
              </dl>
            </section>
          ))}
        </div>
      ) : (
        <div className="error-banner">
          No Reseller plans found. Open Packages → Reseller Plans and ensure
          Reseller Silver / Gold / Platinum exist.
        </div>
      )}

      {show ? (
        <form className="panel-form" onSubmit={(e) => void submit(e)}>
          <div className="panel-form-title">
            {editId ? "Modify Reseller" : "Create a Reseller"}
          </div>
          {error ? <div className="error-banner">{error}</div> : null}
          <div className="form-grid">
            {!editId ? (
              <>
                <div className="field">
                  <label>Username</label>
                  <input
                    value={form.username}
                    onChange={(e) => set("username", e.target.value)}
                    required
                    pattern="[a-z0-9][a-z0-9._-]{2,31}"
                    placeholder="acmehost"
                  />
                </div>
                <div className="field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <div className="field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="field">
                <label>Reseller</label>
                <input value={form.username} disabled className="mono" />
              </div>
            )}
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Reseller plan</label>
              <select
                value={form.packageId}
                onChange={(e) => applyPlan(e.target.value)}
                required
              >
                <option value="">— Select plan —</option>
                {resellerPlans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · up to {p.maxAccounts.toLocaleString()} cPanel
                    accounts · {money(p.priceMonthly)}/mo ·{" "}
                    {money(p.priceYearly)}/yr
                  </option>
                ))}
              </select>
              <p className="card-meta" style={{ marginTop: 6 }}>
                Selecting a plan fills account / disk pools from the package
                (owner can still tweak below).
              </p>
            </div>
            <div className="field">
              <label>Company</label>
              <input
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                placeholder="Acme Hosting LLC"
              />
            </div>
            <div className="field">
              <label>Product name (white-label)</label>
              <input
                value={form.productName}
                onChange={(e) => set("productName", e.target.value)}
                placeholder="Acme Panel"
              />
            </div>
            <div className="field">
              <label>Support URL</label>
              <input
                value={form.supportUrl}
                onChange={(e) => set("supportUrl", e.target.value)}
                placeholder="https://support.example.com"
              />
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Nameservers (one per line)</label>
              <textarea
                className="textarea"
                rows={2}
                value={form.nameservers}
                onChange={(e) => set("nameservers", e.target.value)}
                placeholder={"ns1.example.com\nns2.example.com"}
              />
            </div>
            <div className="field">
              <label>Max cPanel accounts</label>
              <input
                type="number"
                min={1}
                value={form.maxAccounts}
                onChange={(e) => set("maxAccounts", Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label>Max domains (pool)</label>
              <input
                type="number"
                min={1}
                value={form.maxDomains}
                onChange={(e) => set("maxDomains", Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label>Disk pool (MB)</label>
              <input
                type="number"
                min={100}
                value={form.diskMb}
                onChange={(e) => set("diskMb", Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label>Max databases (pool)</label>
              <input
                type="number"
                min={0}
                value={form.maxDatabases}
                onChange={(e) => set("maxDatabases", Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label>Max emails (pool)</label>
              <input
                type="number"
                min={0}
                value={form.maxEmails}
                onChange={(e) => set("maxEmails", Number(e.target.value))}
              />
            </div>
          </div>
          <button
            className="btn btn-primary btn-inline"
            disabled={busy}
            type="submit"
          >
            {busy
              ? "Saving…"
              : editId
                ? "Save reseller"
                : "Create reseller"}
          </button>
        </form>
      ) : null}

      <div className="whm-stats" style={{ marginBottom: 16 }}>
        <div className="whm-stat">
          <span className="whm-stat-label">Resellers</span>
          <span className="whm-stat-value">{resellers.length}</span>
          <span className="whm-stat-meta">Active white-label partners</span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Customer accounts</span>
          <span className="whm-stat-value">
            {resellers.reduce((s, r) => s + (r.usage?.accounts || 0), 0)}
          </span>
          <span className="whm-stat-meta">cPanel accounts under resellers</span>
        </div>
      </div>

      <div className="pkg-grid">
        {resellers.map((r) => {
          const used = r.usage?.accounts || 0;
          const p = pct(used, r.maxAccounts);
          return (
            <section key={r.userId} className="card pkg-card">
              <div className="pkg-head">
                <div>
                  <div className="card-value" style={{ fontSize: 18 }}>
                    {r.username}
                  </div>
                  <p className="card-meta">
                    {r.company || r.productName || "—"}
                  </p>
                </div>
                <span className="badge primary">
                  {r.packageName || "Custom pool"}
                </span>
              </div>
              <dl className="kv compact">
                <dt>accounts</dt>
                <dd className="mono">
                  {used} / {r.maxAccounts.toLocaleString()} ({p}%)
                </dd>
                <dt>disk pool</dt>
                <dd className="mono">{r.diskMb.toLocaleString()} MB</dd>
                <dt>domains pool</dt>
                <dd className="mono">{r.maxDomains.toLocaleString()}</dd>
              </dl>
              <div className="row-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => startEdit(r)}
                >
                  Edit plan / pools
                </button>
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}
